import { Injectable } from '@angular/core';
import { catchAndGetFromFailureRxOp } from '@geekbears/gb-ngrx-stack-base';
import { RequestsActions } from '@modules/regular-user/requests/application/store/requests.actions';
import { Actions, createEffect, EffectsModule, ofType } from '@ngrx/effects';
import { filter, map, mergeMap, withLatestFrom } from 'rxjs';
import { ApprovesServices } from '../../domain/services/approves.service';
import { ApprovesActions } from './approves.actions';
import { ensureAppUserSelector } from '@core/application';
import { Store } from '@ngrx/store';
import { requestToApproveByIdSelector } from './approves.selectors';

@Injectable()
export class ApprovesEffects {
  constructor(
    private actions$: Actions,
    private approvesService: ApprovesServices,
    private store$: Store
  ) {}

  allApproves$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ApprovesActions.getAllApproves),
      mergeMap((action) => {
        return this.approvesService.getAllApproves(action.companyId).pipe(
          map((response) =>
            ApprovesActions.getAllApprovesSuccess({ response })
          ),
          catchAndGetFromFailureRxOp((failure) =>
            ApprovesActions.getAllApprovesFailure({ failure })
          )
        );
      })
    );
  });

  exportApproves$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ApprovesActions.exportApproves),
      mergeMap((action) => {
        return this.approvesService.exportApproves(action.approves).pipe(
          map((response) =>
            ApprovesActions.exportApprovesSuccess({ response })
          ),
          catchAndGetFromFailureRxOp((failure) =>
            ApprovesActions.exportApprovesFailure({ failure })
          )
        );
      })
    );
  });

  getRequestById$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ApprovesActions.getRequestById),
      mergeMap((action) => {
        return this.approvesService.getApproveById(action.requestId).pipe(
          map((response) =>
            ApprovesActions.getRequestByIdSuccess({ response })
          ),
          catchAndGetFromFailureRxOp((failure) =>
            ApprovesActions.getRequestByIdFailure({ failure })
          )
        );
      })
    );
  });

  dispatchGetRequestIdOnActionResult$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        ApprovesActions.denyARequestSuccess,
        ApprovesActions.denyARequestFailure,
        ApprovesActions.approveARequestSuccess,
        ApprovesActions.denyARequestFailure
      ),
      withLatestFrom(this.store$.select(requestToApproveByIdSelector)),
      filter(([action, request]) => {
        return request.data?._id === action.requestId;
      }),
      map(([action, request]) => {
        return ApprovesActions.getRequestById({
          requestId: action.requestId,
        });
      })
    );
  });

  approveARequest$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ApprovesActions.approveARequest),
      mergeMap(({ requestId, message }) => {
        return this.approvesService.approveARequest(requestId, message).pipe(
          map((response) =>
            ApprovesActions.approveARequestSuccess({ response, requestId })
          ),
          catchAndGetFromFailureRxOp((failure) =>
            ApprovesActions.approveARequestFailure({ failure, requestId })
          )
        );
      })
    );
  });

  denyARequest$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ApprovesActions.denyARequest),
      mergeMap(({ requestId, message }) => {
        return this.approvesService.denyARequest(requestId, message).pipe(
          map((response) =>
            ApprovesActions.denyARequestSuccess({ response, requestId })
          ),
          catchAndGetFromFailureRxOp((failure) =>
            ApprovesActions.denyARequestFailure({ failure, requestId })
          )
        );
      })
    );
  });

  myApproves$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ApprovesActions.myApproves),
      mergeMap((action) => {
        const { type, ...params } = action;
        return this.approvesService.myApproves(params).pipe(
          map((response) => ApprovesActions.myApprovesSuccess({ response })),
          catchAndGetFromFailureRxOp((failure) =>
            ApprovesActions.myApprovesFailure({ failure })
          )
        );
      })
    );
  });

  fetch$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ApprovesActions.pendingApprovalsData),
      mergeMap((action) => {
        const { type, ...params } = action;
        // console.log(action);
        return [
          ApprovesActions.pendingApprovals(params),
          ApprovesActions.approvalsCount(params),
        ];
      })
    );
  });

  approvalsCount$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ApprovesActions.approvalsCount),
      withLatestFrom(ensureAppUserSelector(this.store$)),
      mergeMap(([action, user]) => {
        const { type, ...params } = action;
        if (params.limit) {
          delete params.limit;
        }
        if (user.roles.includes('requester')) {
        }

        return this.approvesService
          .approvalsCount({
            populate: ['requester', 'approvals', 'action'],
            filter: action.filter,
          })
          .pipe(
            map((response) =>
              ApprovesActions.approvalsCountSuccess({ response })
            ),
            catchAndGetFromFailureRxOp((failure) =>
              ApprovesActions.approvalsCountFailure({ failure })
            )
          );
      })
    );
  });

  pendingApprovals$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ApprovesActions.pendingApprovals),
      withLatestFrom(ensureAppUserSelector(this.store$)),
      mergeMap(([action, user]) => {
        const { type, ...params } = action;
        return this.approvesService
          .pendingApprovals({
            sort: '-createdAt',
            offset: params.offset ?? 0,
            limit: params.limit ?? 0,
            populate: ['requester', 'approvals', 'action'],
            filter: {
              status: 'pending',
              approvals: {
                $elemMatch: {
                  _approver: user.id,
                  status: 'pending',
                },
              },
            },
          })
          .pipe(
            map((response) =>
              ApprovesActions.pendingApprovalsSuccess({ response })
            ),
            catchAndGetFromFailureRxOp((failure) =>
              ApprovesActions.pendingApprovalsFailure({ failure })
            )
          );
      })
    );
  });

  approversByCompany$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ApprovesActions.getApproversByCompany),
      mergeMap((action) => {
        return this.approvesService
          .getApproversByCompany(action.companyId)
          .pipe(
            map((response) =>
              ApprovesActions.getApproversByCompanySuccess({ response })
            ),
            catchAndGetFromFailureRxOp((failure) =>
              ApprovesActions.getApproversByCompanyFailure({ failure })
            )
          );
      })
    );
  });

  fetchHistory$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ApprovesActions.historyParamsAction),
      mergeMap((action) => {
        const { type, ...params } = action;
        return [
          ApprovesActions.myApproves(params),
          RequestsActions.myRequests(params),
          ApprovesActions.approvalsCount(params),
        ];
      })
    );
  });
}
export const ApprovesEffectsModule = EffectsModule.forFeature([
  ApprovesEffects,
]);
